import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, PlaneTakeoff, FileQuestion, Target, Unlock, BadgeCheck, ShieldUser, RefreshCw } from 'lucide-react';

const PROBLEM_ICONS = [AlertTriangle, PlaneTakeoff, FileQuestion];
const APPROACH_ICONS = [Target, Unlock, BadgeCheck, ShieldUser, RefreshCw];

const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  const problems = t('aboutPage.problems', { returnObjects: true }) as { title: string; desc: string }[];
  const approaches = t('aboutPage.approaches', { returnObjects: true }) as { title: string; desc: string }[];

  return (
    <div className="flex flex-col">
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-muted py-24 px-6 text-center space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            {t('aboutPage.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('aboutPage.subtitle')}
          </p>
          <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed border-t border-border pt-6 mt-2">
            {t('aboutPage.intro')}
          </p>
        </section>

        <div className="max-w-4xl mx-auto px-6 py-20 space-y-24">
          {/* Problems */}
          <section className="space-y-10">
            <h2 className="text-3xl font-semibold tracking-tight text-foreground">
              {t('aboutPage.problemsTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {problems.map((p, i) => {
                const Icon = PROBLEM_ICONS[i];
                return (
                  <Card key={i} className="border border-destructive/20 bg-destructive/5 shadow-none">
                    <CardContent className="pt-6 space-y-3">
                      <Icon className="w-6 h-6 text-destructive" />
                      <h3 className="font-semibold text-foreground">{p.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Approach */}
          <section className="space-y-10">
            <h2 className="text-3xl font-semibold tracking-tight text-foreground">
              {t('aboutPage.approachTitle')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {approaches.map((a, i) => {
                const Icon = APPROACH_ICONS[i];
                return (
                  <Card key={i} className="bg-card border-none shadow-sm">
                    <CardContent className="pt-6 space-y-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground">{a.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{a.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Belief + Goal */}
          <section className="space-y-6">
            <h2 className="text-3xl font-semibold tracking-tight text-foreground">
              {t('aboutPage.beliefTitle')}
            </h2>
            <Card className="bg-card border-none shadow-sm">
              <CardContent className="pt-6 space-y-6">
                <p className="text-muted-foreground leading-relaxed text-base">
                  {t('aboutPage.beliefText')}
                </p>
                <p className="text-lg font-semibold text-primary border-t border-border pt-6">
                  {t('aboutPage.goalText')}
                </p>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;
